import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Edit,
  Calendar
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import userService from '../../services/userService'

const UserMasterView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [userData, setUserData] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setInitialLoading(true)
        const response = await userService.getUserById(id)
        
        if (response.success) {
          const transformedUser = userService.transformUserForDisplay(response.data)
          setUserData({
            userCode: transformedUser.userCode || '',
            name: transformedUser.name || '',
            email: transformedUser.email || '',
            phone: transformedUser.phone || '',
            role: transformedUser.role?.roleName || '',
            department: transformedUser.department?.name || '',
            designation: transformedUser.designation || '',
            status: transformedUser.status || '',
            password: '••••••••', // Masked password for security
            confirmPassword: '••••••••', // Masked password for security
            createdDate: transformedUser.createdDate || new Date().toISOString().split('T')[0]
          })
        } else {
          toast.error('User not found')
          navigate('/settings/user-master')
        }
      } catch (error) {
        console.error('Error loading user:', error)
        toast.error('Failed to load user data')
        navigate('/settings/user-master')
      } finally {
        setInitialLoading(false)
      }
    }

    if (id) {
      loadUserData()
    }
  }, [id, navigate])

  const handleBack = () => {
    navigate('/settings/user-master')
  }

  const handleEdit = () => {
    navigate(`/settings/user-master/edit/${id}`)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'User Master',
      path: '/settings/user-master',
      isCurrent: true
    }
  ]

  if (initialLoading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>User not found</h1>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>User Master - View</h1>
          <p>View user account and permissions</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleEdit}>
            <Edit size={18} />
            Edit
          </button>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>User Master</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: User Code, Full Name, Email Address, Phone Number */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="userCode" className="form-label required">
                User Code
              </label>
              <input
                type="text"
                id="userCode"
                name="userCode"
                value={userData.userCode}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter user code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name" className="form-label required">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={userData.name}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={userData.email}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter email address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={userData.phone}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Row 2: Role, Department, Designation, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="role" className="form-label required">
                Role
              </label>
              <input
                type="text"
                id="role"
                name="role"
                value={userData.role}
                readOnly
                className="form-input readonly-input"
                placeholder="Select Role"
              />
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label required">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={userData.department}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation" className="form-label required">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={userData.designation}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter designation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <input
                type="text"
                id="status"
                name="status"
                value={userData.status}
                readOnly
                className="form-input readonly-input"
                placeholder="Status"
              />
            </div>
          </div>

          {/* Row 3: Password, Confirm Password, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="password" className="form-label required">
                Password
              </label>
              <input
                type="text"
                id="password"
                name="password"
                value={userData.password}
                readOnly
                className="form-input readonly-input"
                placeholder="Enter password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label required">
                Confirm Password
              </label>
              <input
                type="text"
                id="confirmPassword"
                name="confirmPassword"
                value={userData.confirmPassword}
                readOnly
                className="form-input readonly-input"
                placeholder="Confirm password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="createdDate"
                  name="createdDate"
                  value={userData.createdDate}
                  readOnly
                  className="form-input date-input readonly-input"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserMasterView